import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2, Merge, Users } from 'lucide-react';
import { toast } from 'sonner';
import { customersAPI } from '../services/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

const MATCH_LABEL = { name: 'Similar name', gst: 'Same GST number', email: 'Same email' };

const usageLine = (usage) => [
  ['inquiries', usage.inquiries], ['quotations', usage.quotations], ['orders', usage.sales_orders],
  ['POs', usage.customer_pos], ['parts', usage.parts],
  ['name-only inquiries', usage.inquiries_by_name],
].filter(([, count]) => count > 0).map(([label, count]) => `${count} ${label}`).join(' · ') || 'no linked records';

export default function DuplicateCustomersPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [masters, setMasters] = useState({});
  const [confirmGroup, setConfirmGroup] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await customersAPI.duplicates();
      setData(response.data);
      setMasters(Object.fromEntries((response.data.groups || []).map((group, index) =>
        [index, group.suggested_master_id])));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Only an admin can review duplicates');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const merge = async () => {
    const { group, index } = confirmGroup;
    const masterId = masters[index];
    setBusy(true);
    try {
      const response = await customersAPI.merge({
        master_id: masterId,
        duplicate_ids: group.members.filter((member) => member.id !== masterId).map((member) => member.id),
        confirm: confirmText,
      });
      toast.success(response.data.message);
      setConfirmGroup(null); setConfirmText('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    } finally { setBusy(false); }
  };

  if (!data) {
    return <div className="flex min-h-[400px] items-center justify-center" data-testid="duplicates-loading"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="duplicate-customers-page">
      <div>
        <Button variant="ghost" className="mb-2 px-0" onClick={() => navigate('/customers')} data-testid="duplicates-back">
          <ArrowLeft className="mr-2 h-4 w-4" /> Customers
        </Button>
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Duplicate customers</h1>
        <p className="text-muted-foreground">
          {data.group_count} possible duplicate group(s), {data.duplicate_count} extra record(s). Pick the copy to keep —
          every inquiry, quotation, order, PO and part on the others is re-pointed to it before they are retired to the
          recycle bin. Nothing is erased.
        </p>
      </div>

      {data.groups.length === 0 ? (
        <div className="border bg-white py-20 text-center" data-testid="duplicates-empty">
          <Users className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
          <p className="font-medium">No duplicate customers found.</p>
        </div>
      ) : data.groups.map((group, index) => (
        <section key={`${group.match_on}-${group.value}`} className="border bg-white p-5"
          data-testid={`duplicate-group-${index}`}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{MATCH_LABEL[group.match_on]}</Badge>
              <span className="font-mono text-xs text-muted-foreground">{group.value}</span>
            </div>
            <Button size="sm" onClick={() => { setConfirmGroup({ group, index }); setConfirmText(''); }}
              data-testid={`duplicate-merge-btn-${index}`}>
              <Merge className="mr-2 h-4 w-4" /> Merge {group.members.length - 1} into selected
            </Button>
          </div>
          <div className="space-y-2">
            {group.members.map((member) => {
              const isMaster = masters[index] === member.id;
              return (
                <label key={member.id}
                  className={`flex cursor-pointer flex-wrap items-center gap-3 border p-3 transition-colors duration-150 ${
                    isMaster ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                  data-testid={`duplicate-member-${member.id}`}>
                  <input type="radio" name={`master-${index}`} checked={isMaster}
                    onChange={() => setMasters((current) => ({ ...current, [index]: member.id }))}
                    data-testid={`duplicate-master-radio-${member.id}`} />
                  <span className="font-mono text-xs font-semibold">{member.customer_code || '—'}</span>
                  <span className="font-medium">{member.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {[member.city, member.country].filter(Boolean).join(', ')} · {member.customer_type}
                  </span>
                  <span className="ml-auto text-xs text-slate-600">{usageLine(member.usage)}</span>
                  {isMaster && <Badge className="bg-blue-700 text-white">Keep this one</Badge>}
                </label>
              );
            })}
          </div>
        </section>
      ))}

      <Dialog open={Boolean(confirmGroup)} onOpenChange={(open) => { if (!open) setConfirmGroup(null); }}>
        <DialogContent data-testid="duplicate-merge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Confirm customer merge
            </DialogTitle>
            <DialogDescription>
              This rewrites the customer link on historical commercial records. Every change is written to the audit
              log, and the duplicate customers stay restorable in the recycle bin.
            </DialogDescription>
          </DialogHeader>
          {confirmGroup && (
            <div className="space-y-3 py-2 text-sm">
              <p>
                Keeping <strong>{confirmGroup.group.members.find((member) => member.id === masters[confirmGroup.index])?.name}</strong>
              </p>
              <ul className="list-inside list-disc text-muted-foreground">
                {confirmGroup.group.members.filter((member) => member.id !== masters[confirmGroup.index]).map((member) => (
                  <li key={member.id}>{member.name} — {usageLine(member.usage)}</li>
                ))}
              </ul>
              <Input value={confirmText} onChange={(event) => setConfirmText(event.target.value)}
                placeholder='Type MERGE to confirm' data-testid="duplicate-merge-confirm-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmGroup(null)} data-testid="duplicate-merge-cancel">Cancel</Button>
            <Button onClick={merge} disabled={busy || confirmText.trim().toUpperCase() !== 'MERGE'}
              data-testid="duplicate-merge-confirm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Merge className="mr-2 h-4 w-4" />} Merge now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
